import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, formatINR } from "@/lib/api";
import TenantLogo from "@/components/TenantLogo";
import { useTenant } from "./TenantRoot";
import { Search, Star, ChevronRight, Tag, Sparkles } from "lucide-react";

export default function TenantHome() {
  const { slug } = useParams();
  const { tenant } = useTenant();
  const [categories, setCategories] = useState([]);
  const [services, setServices] = useState([]);
  const [sellers, setSellers] = useState([]);

  useEffect(() => {
    Promise.all([
      api.get(`/public/tenant/${slug}/categories`),
      api.get(`/public/tenant/${slug}/services`),
      api.get(`/public/tenant/${slug}/sellers`),
    ]).then(([c, s, sl]) => {
      setCategories(c.data); setServices(s.data); setSellers(sl.data);
    });
  }, [slug]);

  const popular = services.slice(0, 8);
  const appName = tenant.branding?.app_name || tenant.business_name;

  return (
    <div data-testid="tenant-home" className="pb-6 md:py-8">
      {/* Intro + banner: stacked on phones, side by side from 768px */}
      <div className="md:grid md:grid-cols-2 md:gap-8 md:items-center md:px-6">
        <div className="home-intro px-5 pt-6 pb-4 md:p-0" data-testid="tenant-home-header">
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <div className="text-xs md:text-sm brand-text-muted">Welcome to</div>
              <h1 className="brand-heading font-bold text-2xl md:text-4xl tracking-tight leading-tight break-words">{appName}</h1>
            </div>
            <div className="mobile-only flex-shrink-0">
              <TenantLogo tenant={tenant} className="h-12 w-12 rounded-xl text-lg" />
            </div>
          </div>
          {tenant.branding?.tagline && <p className="mt-2 text-sm md:text-base brand-text-muted">{tenant.branding.tagline}</p>}

          <Link to={`/t/${slug}/search`} className="mt-5 flex items-center gap-2 brand-muted rounded-full px-4 py-3 md:max-w-md" data-testid="tenant-home-search">
            <Search className="h-4 w-4 brand-text-muted flex-shrink-0" />
            <span className="text-sm brand-text-muted truncate">Search for a service…</span>
          </Link>
        </div>

        <div className="px-5 pt-4 md:p-0">
          <div className="relative rounded-2xl overflow-hidden anim-fade-up aspect-[16/9]">
            <img
              src={tenant.branding?.banner_url || "https://images.pexels.com/photos/6195275/pexels-photo-6195275.jpeg"}
              alt="" className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-5 text-white">
              <div className="text-xs uppercase tracking-wider opacity-80 flex items-center gap-1.5"><Sparkles className="h-3 w-3" /> Featured</div>
              <div className="text-2xl font-bold brand-heading mt-1 leading-tight">Trusted pros at<br/>your doorstep.</div>
            </div>
          </div>
        </div>
      </div>

      {/* Categories */}
      {categories.length > 0 && (
        <section className="mt-7 md:mt-12 px-5 md:px-6">
          <h2 className="brand-heading font-bold text-lg md:text-xl">Categories</h2>
          <div className="grid grid-cols-4 sm:grid-cols-5 lg:grid-cols-8 gap-3 md:gap-4 mt-3" data-testid="tenant-home-categories">
            {categories.map((c) => (
              <Link key={c.id} to={`/t/${slug}/category/${c.id}`} className="flex flex-col items-center gap-2 min-w-0" data-testid={`tenant-category-${c.slug}`}>
                <div className="aspect-square w-full rounded-2xl overflow-hidden brand-muted">
                  {c.image_url && <img src={c.image_url} alt="" className="h-full w-full object-cover" />}
                </div>
                <div className="text-[11px] md:text-xs font-semibold text-center leading-tight break-words w-full">{c.name}</div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Popular services: a swipe rail on phones, a grid from 768px */}
      {popular.length > 0 && (
        <section className="mt-8 md:mt-12">
          <div className="flex items-center justify-between px-5 md:px-6">
            <h2 className="brand-heading font-bold text-lg md:text-xl">Popular services</h2>
            <Link to={`/t/${slug}/search`} className="text-xs md:text-sm font-semibold brand-primary-text py-2">View all</Link>
          </div>
          <div className="scroll-rail rail-grid mt-3" data-testid="tenant-home-popular">
            {popular.map((s) => (
              <Link key={s.id} to={`/t/${slug}/service/${s.id}`} className="brand-card w-44 md:w-auto overflow-hidden" data-testid={`tenant-popular-${s.id}`}>
                <div className="aspect-[4/3] brand-muted overflow-hidden">
                  {s.images?.[0] && <img src={s.images[0]} alt="" className="h-full w-full object-cover" />}
                </div>
                <div className="p-3">
                  <div className="text-xs flex items-center gap-1 brand-text-muted"><Star className="h-3 w-3 fill-current" style={{ color: "#FBBF24" }} /> {s.rating?.toFixed(1) || "4.5"} · {s.duration_minutes}m</div>
                  <div className="text-sm font-semibold mt-1 leading-tight line-clamp-2">{s.name}</div>
                  <div className="mt-1 flex items-baseline gap-1.5">
                    <div className="text-sm font-bold">{formatINR(s.discount_price || s.price)}</div>
                    {s.discount_price && <div className="text-[11px] line-through brand-text-muted">{formatINR(s.price)}</div>}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {categories.length === 0 && services.length === 0 && (
        <div className="px-5 md:px-6 mt-10 text-center text-sm brand-text-muted" data-testid="tenant-home-empty">
          Services will appear here soon.
        </div>
      )}

      {/* Offers banner */}
      <section className="mt-7 md:mt-12 px-5 md:px-6">
        <div className="rounded-2xl p-5 flex items-center gap-3 md:max-w-xl" style={{ background: "hsl(var(--brand-primary) / 0.1)" }} data-testid="tenant-home-offer">
          <Tag className="h-5 w-5 brand-primary-text flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="text-sm font-bold">First booking? Get 10% off</div>
            <div className="text-xs brand-text-muted mt-0.5">Use code <span className="font-mono font-bold">WELCOME10</span> at checkout</div>
          </div>
        </div>
      </section>

      {/* Top sellers */}
      {sellers.length > 0 && (
        <section className="mt-7 md:mt-12">
          <h2 className="brand-heading font-bold text-lg md:text-xl px-5 md:px-6">Top professionals</h2>
          <div className="scroll-rail rail-grid mt-3" data-testid="tenant-home-sellers">
            {sellers.slice(0, 8).map((s) => (
              <div key={s.id} className="brand-card w-40 md:w-auto p-3 text-center">
                <div className="h-16 w-16 rounded-full overflow-hidden mx-auto brand-muted">
                  {s.avatar_url && <img src={s.avatar_url} alt="" className="h-full w-full object-cover" />}
                </div>
                <div className="text-xs font-semibold mt-2 leading-tight break-words">{s.business_name}</div>
                <div className="text-[10px] brand-text-muted mt-0.5 flex items-center justify-center gap-1">
                  <Star className="h-2.5 w-2.5 fill-current" style={{ color: "#FBBF24" }} /> {s.rating?.toFixed(1)} · {s.total_jobs} jobs
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Enquiry CTA */}
      <section className="mt-7 md:mt-12 px-5 md:px-6">
        <Link to={`/t/${slug}/enquiry`} className="block brand-card p-5 border-2 brand-border md:max-w-xl" data-testid="tenant-home-enquiry-cta">
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <div className="text-xs uppercase tracking-wider brand-text-muted font-semibold">Not sure?</div>
              <div className="brand-heading font-bold text-lg mt-1">Send an enquiry</div>
              <div className="text-xs brand-text-muted mt-0.5">Tell us what you need and we will get back to you.</div>
            </div>
            <ChevronRight className="h-5 w-5 brand-primary-text flex-shrink-0" />
          </div>
        </Link>
      </section>
    </div>
  );
}
